import { NestAPI, useGetBusiness } from '@marketing-milk/frontend'
import {
  BrandLogo,
  CreateSprintPacket,
  SprintPacket,
  SprintPacketHydrated,
  UpdateSprintPacket,
} from '@marketing-milk/interfaces'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

type Body<T> = { body: T }

export const useSprintPackets = () => {
  const business = useGetBusiness()
  const endpoint = `businesses/${business.id}/sprint-packets`

  const {
    data: list,
    refetch,
    isLoading,
    isFetched,
    isError,
  } = useQuery(
    ['sprint-packets', business.id],
    async () => {
      const response = await NestAPI.get<Body<SprintPacket[]>>(endpoint)
      return response.data.body
    },
    { retry: false }
  )

  const create = async (dto: CreateSprintPacket): Promise<SprintPacket | undefined> => {
    try {
      const response = await NestAPI.post<Body<SprintPacket>>(endpoint, dto)
      await refetch()
      return response.data.body
    } catch (err) {
      toast.error(`Create action failed: ${err}`)
      return undefined
    }
  }

  return { list, create, refetch, isLoading, isFetched, isError }
}

export const useSprintPacket = (
  packetID: number | undefined,
  // Present's survey slide polls the packet while active (live Submitted
  // count) — pass a millisecond interval, or false/omit for a normal fetch.
  options?: { refetchInterval?: number | false }
) => {
  const business = useGetBusiness()

  const {
    data: packet,
    refetch,
    isLoading,
    isFetched,
    isError,
  } = useQuery(
    ['sprint-packet', business.id, packetID],
    async () => {
      const response = await NestAPI.get<Body<SprintPacketHydrated>>(
        `businesses/${business.id}/sprint-packets/${packetID}`
      )
      return response.data.body
    },
    {
      retry: false,
      enabled: typeof packetID === 'number' && !Number.isNaN(packetID),
      refetchInterval: options?.refetchInterval ?? false,
    }
  )

  return { packet, refetch, isLoading, isFetched, isError }
}

// Uploads one brand-logo image to the business's private S3 folder and returns
// its durable object key + a signed preview URL. The key only becomes part of
// the packet when the strategist saves it via `brandStandards`.
export const useUploadBrandLogo = () => {
  const business = useGetBusiness()

  return async (file: File): Promise<BrandLogo> => {
    const formData = new FormData()
    formData.append('image', file)
    const response = await NestAPI.post<Body<BrandLogo>>(
      `businesses/${business.id}/sprint-packets/brand-logos`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data.body
  }
}

export const useUpdateSprintPacket = (packetID: number | undefined) => {
  const business = useGetBusiness()
  const queryClient = useQueryClient()

  return useMutation(
    async (dto: UpdateSprintPacket): Promise<SprintPacket> => {
      const response = await NestAPI.patch<Body<SprintPacket>>(
        `businesses/${business.id}/sprint-packets/${packetID}`,
        dto
      )
      return response.data.body
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sprint-packet', business.id, packetID])
      },
      onError: err => {
        toast.error(`Save failed: ${err}`)
      },
    }
  )
}
